import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  FaArrowRight,
  FaBrain,
  FaWifi,
  FaCode,
  FaCloud,
  FaShieldAlt,
  FaChartLine,
  FaQuoteLeft,
} from 'react-icons/fa';
import './Home.css';

/* ── Motion variants ── */
const heroStagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.13, delayChildren: 0.08 } },
};
const heroItem = {
  hidden: { opacity: 0, y: 26, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

/* ── Animated counter hook ── */
const useCounter = (target, duration = 1800, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
};

const StatItem = ({ value, suffix, label, started }) => {
  const count = useCounter(parseInt(value), 1600, started);
  return (
    <div className="stat-item">
      <span className="stat-number">{started ? count : 0}{suffix}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
};

const Home = () => {
  const [statsStarted, setStatsStarted] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsStarted(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  const services = [
    {
      icon: <FaBrain />,
      title: 'AI & Generative AI',
      description:
        'LLM integrations, AI agents, RAG knowledge systems, and intelligent process automation — built on GPT-4, Claude, and Gemini and wired into the systems you already run.',
      tags: ['LLM Integration', 'AI Agents', 'RAG', 'Automation'],
      featured: true,
    },
    {
      icon: <FaWifi />,
      title: 'IoT & Smart Systems',
      description:
        'Smart sensors, edge computing, industrial automation, and real-time monitoring dashboards.',
      tags: ['Industrial IoT', 'MQTT'],
    },
    {
      icon: <FaCode />,
      title: 'Web & App Development',
      description:
        'High-performance web apps, PWAs, and cross-platform mobile apps built with modern stacks.',
      tags: ['React / Next.js', 'Mobile'],
    },
    {
      icon: <FaCloud />,
      title: 'Cloud & DevOps',
      description:
        'Cloud architecture, CI/CD pipelines, Docker & Kubernetes, and serverless on AWS/Azure.',
      tags: ['AWS / Azure', 'Kubernetes'],
    },
    {
      icon: <FaShieldAlt />,
      title: 'Cybersecurity',
      description:
        'Zero-trust architecture, penetration testing, compliance audits, and threat detection.',
      tags: ['Pen Testing', 'Compliance'],
    },
    {
      icon: <FaChartLine />,
      title: 'Data Intelligence',
      description:
        'Data pipelines, ML models, real-time BI dashboards, and predictive analytics that turn the data you already collect into decisions.',
      tags: ['Power BI', 'Python ML', 'Pipelines'],
      wide: true,
    },
  ];

  const stats = [
    { value: 80, suffix: '+', label: 'Projects delivered' },
    { value: 80, suffix: '+', label: 'Clients served' },
    { value: 6, suffix: '', label: 'Service lines' },
    { value: 24, suffix: '/7', label: 'Support coverage' },
  ];

  const process = [
    {
      num: '01',
      title: 'Discover',
      desc: 'A free consultation to understand your business, systems, and goals — followed by a clear scoped proposal.',
    },
    {
      num: '02',
      title: 'Design',
      desc: 'Solution architecture, UX, and a delivery plan you sign off on before a single line of code is written.',
    },
    {
      num: '03',
      title: 'Build',
      desc: 'Agile sprints with demos every step — you see working software early and often, never a black box.',
    },
    {
      num: '04',
      title: 'Support',
      desc: 'Deployment, monitoring, and ongoing maintenance with on-call engineers after go-live.',
    },
  ];

  const testimonials = [
    {
      quote:
        'They rebuilt our billing and inventory flow end to end. What used to take our staff hours now happens automatically — and they were reachable at every step.',
      author: 'Operations Head',
      org: 'Retail chain, Kochi',
    },
    {
      quote:
        'The IoT dashboard they built gives us live visibility into every machine on the floor. Downtime alerts alone paid for the project within months.',
      author: 'Plant Manager',
      org: 'Manufacturing unit, Coimbatore',
    },
    {
      quote:
        'We came with a vague idea about “using AI” and left with a working assistant our customers actually use. Honest advice, no overselling.',
      author: 'Founder',
      org: 'Healthcare startup, Bengaluru',
    },
  ];

  const techStack = [
    'React', 'Next.js', 'Node.js', 'Python', 'TensorFlow',
    'OpenAI API', 'AWS', 'Azure', 'Docker', 'Kubernetes',
    'MongoDB', 'PostgreSQL', 'MQTT', 'Raspberry Pi', 'Arduino',
  ];

  return (
    <div className="home-page">

      {/* ── HERO — editorial, full-width ── */}
      <section className="hero">
        <motion.div className="container" variants={heroStagger} initial="hidden" animate="show">
          <motion.p className="hero-overline" variants={heroItem}>Abidott Solutions · Software, AI &amp; IoT engineering · Kerala, India</motion.p>
          <motion.h1 className="hero-headline" variants={heroItem}>
            We build the systems<br />
            your business runs on<span className="accent-dot">.</span>
          </motion.h1>
          <motion.div className="hero-foot" variants={heroItem}>
            <p className="hero-lede">
              AI integrations, IoT platforms, cloud infrastructure, and secure software —
              designed, built, and supported by one accountable team. 80+ projects
              delivered for businesses across India since 2023.
            </p>
            <div className="hero-ctas">
              <a href="#contact" className="btn-primary">
                Start a project <FaArrowRight className="btn-icon" />
              </a>
              <a href="#services" className="btn-outline">See services</a>
            </div>
          </motion.div>
        </motion.div>

        {/* Stat bar */}
        <div className="container">
          <div className="hero-stats" ref={statsRef}>
            {stats.map((s, i) => (
              <StatItem key={i} {...s} started={statsStarted} />
            ))}
          </div>
        </div>
      </section>

      {/* ── TECH STRIP ── */}
      <section className="tech-strip" aria-label="Technologies we work with">
        <div className="tech-marquee">
          <div className="tech-track">
            {[...techStack, ...techStack].map((t, i) => (
              <span key={i} className="tech-item">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES — bento grid ── */}
      <section className="services" id="services">
        <div className="container">
          <div className="sec-head">
            <div>
              <p className="sec-overline">What we do</p>
              <h2 className="sec-title">Six service lines.<br />One accountable partner.</h2>
            </div>
            <a href="#services" className="sec-link">
              All services <FaArrowRight />
            </a>
          </div>

          <div className="bento">
            {services.map((svc, i) => (
              <a href="#services"
                key={i}
                className={`bento-card ${svc.featured ? 'bento-featured' : ''} ${svc.wide ? 'bento-wide' : ''}`}
              >
                <div className="bento-icon">{svc.icon}</div>
                <div className="bento-body">
                  <h3 className="bento-title">{svc.title}</h3>
                  <p className="bento-desc">{svc.description}</p>
                </div>
                <div className="bento-foot">
                  <div className="bento-tags">
                    {svc.tags.map((t, ti) => <span key={ti}>{t}</span>)}
                  </div>
                  <FaArrowRight className="bento-arrow" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section className="process">
        <div className="container">
          <div className="sec-head">
            <div>
              <p className="sec-overline">How we work</p>
              <h2 className="sec-title">No surprises, at any step.</h2>
            </div>
          </div>

          <div className="process-grid">
            {process.map((p, i) => (
              <div key={i} className="process-step">
                <span className="process-num">{p.num}</span>
                <h3 className="process-title">{p.title}</h3>
                <p className="process-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── IOT SPOTLIGHT ── */}
      <section className="spotlight" id="about">
        <div className="container">
          <div className="spotlight-grid">
            <div className="spotlight-intro">
              <p className="sec-overline">IoT &amp; smart systems</p>
              <h2 className="sec-title">From the factory floor to the cloud.</h2>
              <p className="spotlight-lede">
                Our IoT division connects physical operations to software — smart factories,
                precision agriculture, healthcare monitoring, building automation. We handle
                hardware, firmware, connectivity, and the dashboards your team actually uses.
              </p>
              <a href="#services" className="btn-primary">
                Explore IoT solutions <FaArrowRight className="btn-icon" />
              </a>
              <img
                className="spotlight-photo"
                src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=1000&q=80"
                alt="Engineer monitoring industrial systems on a laptop"
                loading="lazy"
              />
            </div>

            <ol className="spotlight-list">
              {[
                ['Industrial IoT & SCADA', 'Machine-level integration with existing plant systems.'],
                ['Real-time telemetry', 'MQTT and WebSocket pipelines from sensor to screen.'],
                ['Edge computing', 'Raspberry Pi and ESP32 deployments that work offline.'],
                ['Live dashboards', 'Cloud-connected monitoring, alerts, and analytics.'],
                ['Fleet management', 'Remote device control and over-the-air updates.'],
              ].map(([title, desc], i) => (
                <li key={i} className="spotlight-item">
                  <span className="spotlight-num">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <h4>{title}</h4>
                    <p>{desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="quotes">
        <div className="container">
          <div className="sec-head">
            <div>
              <p className="sec-overline">Client outcomes</p>
              <h2 className="sec-title">Judged by what we ship.</h2>
            </div>
          </div>

          <div className="quotes-grid">
            {testimonials.map((t, i) => (
              <figure key={i} className="quote-card">
                <FaQuoteLeft className="quote-mark" />
                <blockquote>{t.quote}</blockquote>
                <figcaption>
                  <strong>{t.author}</strong>
                  <span>{t.org}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA — full-bleed band ── */}
      <section className="cta-band" id="contact">
        <div className="container cta-band-inner">
          <div>
            <h2 className="cta-band-title">Have a project in mind?</h2>
            <p className="cta-band-sub">
              Free 30-minute consultation. We'll tell you honestly what's worth building — and what isn't.
            </p>
          </div>
          <div className="cta-band-actions">
            <a href="#contact" className="btn-primary btn-lg">
              Talk to an engineer <FaArrowRight className="btn-icon" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
